import { STRUDEL_FLOW_WIDGET_ID_PREFIX } from './config'
import { isStrudelFlowInstrumentKind } from './nodeDefaults'
import { generateStrudelFlowNodePattern } from './nodePatterns'
import { normalizeStrudelFlowBeatsPerCycle, normalizeStrudelFlowBeatsPerMinute } from './tempo'
import type {
  StrudelFlowCompilation,
  StrudelFlowEdge,
  StrudelFlowNode,
  StrudelFlowPatch
} from './types'

type OrderState = {
  visiting: string[]
  visited: string[]
  ordered: string[]
  hasCycle: boolean
}

const EMPTY_ORDER: OrderState = {
  visiting: [],
  visited: [],
  ordered: [],
  hasCycle: false
}

const buildNeighbourMap = (edges: StrudelFlowEdge[]): Map<string, string[]> =>
  edges.reduce((neighbours, edge) => {
    const sourceNeighbours = neighbours.get(edge.source) ?? []
    const targetNeighbours = neighbours.get(edge.target) ?? []
    return new Map([
      ...neighbours,
      [edge.source, [...sourceNeighbours, edge.target]],
      [edge.target, [...targetNeighbours, edge.source]]
    ])
  }, new Map<string, string[]>())

const buildOutgoingMap = (
  edges: StrudelFlowEdge[],
  componentIds: string[]
): Map<string, string[]> =>
  edges.reduce((outgoing, edge) => {
    if (!componentIds.includes(edge.source) || !componentIds.includes(edge.target)) {
      return outgoing
    }
    return new Map([
      ...outgoing,
      [edge.source, [...(outgoing.get(edge.source) ?? []), edge.target]]
    ])
  }, new Map<string, string[]>())

const collectComponent = (
  nodeId: string,
  neighbours: Map<string, string[]>,
  visited: string[]
): { component: string[]; visited: string[] } => {
  if (visited.includes(nodeId)) {
    return { component: [], visited }
  }
  const nextVisited = [...visited, nodeId]
  return (neighbours.get(nodeId) ?? []).reduce(
    (collected, neighbour) => {
      const downstream = collectComponent(neighbour, neighbours, collected.visited)
      return {
        component: [...collected.component, ...downstream.component],
        visited: downstream.visited
      }
    },
    { component: [nodeId], visited: nextVisited }
  )
}

const findComponents = (nodes: StrudelFlowNode[], edges: StrudelFlowEdge[]): string[][] => {
  const neighbours = buildNeighbourMap(edges)
  return nodes.reduce(
    (result, node) => {
      if (result.visited.includes(node.id)) {
        return result
      }
      const collected = collectComponent(node.id, neighbours, result.visited)
      return {
        components: [...result.components, collected.component],
        visited: collected.visited
      }
    },
    { components: [] as string[][], visited: [] as string[] }
  ).components
}

const visitInSignalOrder = (
  nodeId: string,
  outgoing: Map<string, string[]>,
  state: OrderState
): OrderState => {
  if (state.visiting.includes(nodeId)) {
    return { ...state, hasCycle: true }
  }
  if (state.visited.includes(nodeId)) {
    return state
  }
  const entered = { ...state, visiting: [...state.visiting, nodeId] }
  const afterChildren = (outgoing.get(nodeId) ?? []).reduce(
    (current, target) => visitInSignalOrder(target, outgoing, current),
    entered
  )
  return {
    visiting: afterChildren.visiting.filter((visitingId) => visitingId !== nodeId),
    visited: [...afterChildren.visited, nodeId],
    ordered: [nodeId, ...afterChildren.ordered],
    hasCycle: afterChildren.hasCycle
  }
}

const orderComponent = (componentIds: string[], edges: StrudelFlowEdge[]): OrderState => {
  const outgoing = buildOutgoingMap(edges, componentIds)
  return componentIds.reduce(
    (state, nodeId) => visitInSignalOrder(nodeId, outgoing, state),
    EMPTY_ORDER
  )
}

const findEffectIntoInstrument = (
  edges: StrudelFlowEdge[],
  nodesById: Map<string, StrudelFlowNode>
): StrudelFlowEdge | undefined =>
  edges.find((edge) => {
    const target = nodesById.get(edge.target)
    return target !== undefined && isStrudelFlowInstrumentKind(target.data.kind)
  })

const isMuted = (node: StrudelFlowNode): boolean => 'muted' in node.data && node.data.muted === true

const instrumentsInComponent = (
  componentIds: string[],
  nodesById: Map<string, StrudelFlowNode>
): StrudelFlowNode[] =>
  componentIds
    .map((nodeId) => nodesById.get(nodeId))
    .filter((node): node is StrudelFlowNode => node !== undefined)
    .filter((node) => isStrudelFlowInstrumentKind(node.data.kind))

const collectDownstream = (
  nodeId: string,
  outgoing: Map<string, string[]>,
  reached: string[]
): string[] =>
  (outgoing.get(nodeId) ?? []).reduce(
    (collected, target) =>
      collected.includes(target)
        ? collected
        : collectDownstream(target, outgoing, [...collected, target]),
    reached
  )

const commonSuffixLength = (lists: string[][]): number => {
  const shortest = Math.min(...lists.map((list) => list.length))
  return (
    Array.from({ length: shortest }, (_, offset) => offset + 1).findLast((length) =>
      lists.every((list) => list.slice(-length).join() === lists[0].slice(-length).join())
    ) ?? 0
  )
}

const applyEffects = (
  pattern: string,
  effectIds: string[],
  nodesById: Map<string, StrudelFlowNode>
): string =>
  effectIds.reduce((currentPattern, nodeId) => {
    const node = nodesById.get(nodeId)
    return node === undefined
      ? currentPattern
      : generateStrudelFlowNodePattern(node.data, currentPattern)
  }, pattern)

type InstrumentPath = { pattern: string; effectIds: string[] }

type ComponentPlan = { label: string; branches: InstrumentPath[]; sharedIds: string[] }

// Each instrument only goes through the effects downstream of it. The effects every path shares
// are chained once after the stack; the rest stay on their own branch inside it.
const planComponent = (
  componentIds: string[],
  edges: StrudelFlowEdge[],
  nodesById: Map<string, StrudelFlowNode>
): ComponentPlan | null => {
  const instruments = instrumentsInComponent(componentIds, nodesById)
  const isComponentMuted = instruments.length > 0 && instruments.every(isMuted)
  const outgoing = buildOutgoingMap(edges, componentIds)
  const ordered = orderComponent(componentIds, edges).ordered
  const paths: InstrumentPath[] = instruments.flatMap((node) => {
    const pattern = generateStrudelFlowNodePattern(node.data, '')
    if (pattern.length === 0) {
      return []
    }
    const downstream = collectDownstream(node.id, outgoing, [])
    return [
      {
        pattern: !isComponentMuted && isMuted(node) ? `${pattern}.hush()` : pattern,
        effectIds: ordered.filter((nodeId) => downstream.includes(nodeId))
      }
    ]
  })
  if (paths.length === 0) {
    return null
  }
  const sharedLength = commonSuffixLength(paths.map((path) => path.effectIds))
  return {
    label: isComponentMuted ? '_$:' : '$:',
    branches: paths.map((path) => ({
      pattern: path.pattern,
      effectIds: path.effectIds.slice(0, path.effectIds.length - sharedLength)
    })),
    sharedIds: paths[0].effectIds.slice(paths[0].effectIds.length - sharedLength)
  }
}

const compilePlan = (plan: ComponentPlan, nodesById: Map<string, StrudelFlowNode>): string => {
  const branches = plan.branches.map((branch) =>
    applyEffects(branch.pattern, branch.effectIds, nodesById)
  )
  return `${plan.label} ${applyEffects(stackPatterns(branches), plan.sharedIds, nodesById)}`
}

const planComponents = (
  nodes: StrudelFlowNode[],
  edges: StrudelFlowEdge[],
  nodesById: Map<string, StrudelFlowNode>
): ComponentPlan[] =>
  findComponents(nodes, edges).flatMap((componentIds) => {
    const plan = planComponent(componentIds, edges, nodesById)
    return plan === null ? [] : [plan]
  })

const stackPatterns = (patterns: string[]): string =>
  patterns.length === 1 ? patterns[0] : `stack(${patterns.join(', ')})`

/**
 * Turns a flow patch into one Strudel program: a tempo, the setup statements, then one `$:` block per connected
 * component. A component stacks its instruments; each one passes only through the effects
 * downstream of it, and the effects every instrument shares are chained once after the stack.
 * @param patch - the nodes, edges and tempo to compile
 * @returns the program, or the reason it cannot be played
 */
export const compileStrudelFlow = (patch: StrudelFlowPatch): StrudelFlowCompilation => {
  const nodesById = new Map(patch.nodes.map((node) => [node.id, node]))
  if (findEffectIntoInstrument(patch.edges, nodesById) !== undefined) {
    return {
      code: '',
      errorMessage:
        'An instrument cannot take an input. Connect the effect to another effect instead.'
    }
  }
  if (!patch.nodes.some((node) => isStrudelFlowInstrumentKind(node.data.kind))) {
    return {
      code: '',
      errorMessage: 'Add an instrument node such as Pad, Beats, Chords or Code to start a pattern.'
    }
  }
  const components = findComponents(patch.nodes, patch.edges)
  const cyclicComponent = components.find(
    (componentIds) => orderComponent(componentIds, patch.edges).hasCycle
  )
  if (cyclicComponent !== undefined) {
    return {
      code: '',
      errorMessage: 'Remove the loop from the flow: an effect cannot feed back into itself.'
    }
  }
  const blocks = planComponents(patch.nodes, patch.edges, nodesById).map((plan) =>
    compilePlan(plan, nodesById)
  )
  if (blocks.length === 0) {
    return {
      code: '',
      errorMessage:
        'The flow makes no sound yet. Turn on a drum step, add notes, or write an expression.'
    }
  }
  const tempo = `setcpm(${normalizeStrudelFlowBeatsPerMinute(patch.beatsPerMinute)}/${normalizeStrudelFlowBeatsPerCycle(patch.beatsPerCycle)})`
  const setup = patch.setup.trim()
  return {
    code: [tempo, ...(setup.length > 0 ? [setup] : []), ...blocks].join('\n'),
    errorMessage: null
  }
}

/**
 * The canvas each inline visual such as `._scope()` draws on, which Strudel names after the
 * call and how many calls of that visual come before it in the code.
 * ponytail: a `_scope()` written inside a Code node shifts the count; read the transpiler's
 * widget positions instead if that case ever matters.
 * @param nodes - the patch's nodes
 * @param edges - the patch's cables
 * @returns the canvas element id for every inline Visualizer node that makes it into the code
 */
export const getStrudelFlowVisualizerWidgetIds = (
  nodes: StrudelFlowNode[],
  edges: StrudelFlowEdge[]
): Record<string, string> => {
  const nodesById = new Map(nodes.map((node) => [node.id, node]))
  const inlineVisualizers = planComponents(nodes, edges, nodesById)
    .flatMap((plan) => [...plan.branches.flatMap((branch) => branch.effectIds), ...plan.sharedIds])
    .flatMap((nodeId) => {
      const data = nodesById.get(nodeId)?.data
      return data?.kind === 'visualizer' && data.placement === 'inline'
        ? [{ nodeId, call: `_${data.visual}` }]
        : []
    })
  return Object.fromEntries(
    inlineVisualizers.map(({ nodeId, call }, position) => {
      const index = inlineVisualizers
        .slice(0, position)
        .filter((earlier) => earlier.call === call).length
      return [nodeId, `${STRUDEL_FLOW_WIDGET_ID_PREFIX}${call}_${index}`]
    })
  )
}
